import type { UserPresence } from "@elaso/shared/types/db";
import { Loader2, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useStore } from "zustand";
import { ProfileOrganization } from "@/components/app/profile-organization";
import { Button } from "@/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { type RoleKeyLookup, useRoleKeys } from "@/hooks/use-role-keys";
import { ROLE_DISPLAY_NAMES, ROLE_PRIORITY_ORDER } from "@/lib/role-constants";
import { coreStore, type UserEntry } from "@/lib/stores/core-store";
import { cn } from "@/lib/utils";
import { useChannelContext } from "./channel-view";

type DisplayActivityStatus =
	| "active"
	| "idle"
	| "in-broadcast"
	| "in-voice"
	| "offline";

function getDisplayActivityStatus(
	presence: UserPresence | undefined,
	isInBroadcast: boolean
): DisplayActivityStatus {
	if (!presence || presence.status === "offline") {
		return "offline";
	}
	if (isInBroadcast) {
		return "in-broadcast";
	}
	if (presence.currentChannelId) {
		return "in-voice";
	}
	return presence.activityStatus === "idle" ? "idle" : "active";
}

function PresenceIndicator({
	status,
	className,
}: {
	status: DisplayActivityStatus;
	className?: string;
}) {
	return (
		<span className={cn("relative flex size-3 shrink-0", className)}>
			{status === "active" && (
				<span className="size-full rounded-full bg-green-500" />
			)}
			{status === "idle" && (
				<span className="size-full rounded-full bg-yellow-500" />
			)}
			{status === "in-broadcast" && (
				<span className="size-full rounded-full bg-green-500 ring-2 ring-yellow-400 ring-offset-1 ring-offset-background" />
			)}
			{status === "in-voice" && (
				<span className="size-full rounded-full bg-blue-500" />
			)}
			{status === "offline" && (
				<span className="size-full rounded-full bg-gray-400" />
			)}
		</span>
	);
}

function getStatusLabel(status: DisplayActivityStatus): string {
	switch (status) {
		case "active":
			return "Active";
		case "idle":
			return "Idle";
		case "in-broadcast":
			return "In Broadcast";
		case "in-voice":
			return "In Video";
		case "offline":
			return "Offline";
		default:
			return "Unknown";
	}
}

const LEARNER_SUB_ROLE_PREFIXES = ["team--", "cohort--", "oi-table--"];

/**
 * ROLE NAMES, not keys, are what this popover groups, orders and labels by. The
 * three helpers below all take a name, and the one below them is the single
 * place a key becomes one.
 *
 * Keeping the two apart is load-bearing. A key is the server's opaque word for
 * a role and only the roster can name it; a group name like "learner" is this
 * file's own word and names nothing in the roster. One function serving both
 * would have to guess which it was handed, and would silently answer "everyone"
 * for every group it could not look up.
 */
function formatRoleName(roleName: string): string {
	const name = roleName || "everyone";
	return (
		ROLE_DISPLAY_NAMES[name] ?? name.charAt(0).toUpperCase() + name.slice(1)
	);
}

function roleNamePriority(roleName: string): number {
	const name = roleName || "everyone";
	const idx = ROLE_PRIORITY_ORDER.indexOf(name);
	if (idx >= 0) return idx;
	if (LEARNER_SUB_ROLE_PREFIXES.some((p) => name.startsWith(p))) {
		return ROLE_PRIORITY_ORDER.indexOf("learner");
	}
	return ROLE_PRIORITY_ORDER.indexOf("everyone");
}

/** The group a role name belongs to: its own name, "learner", or "everyone". */
function canonicalGroupName(roleName: string): string {
	const name = roleName || "everyone";
	if (LEARNER_SUB_ROLE_PREFIXES.some((p) => name.startsWith(p)))
		return "learner";
	if (ROLE_PRIORITY_ORDER.includes(name)) return name;
	return "everyone";
}

/**
 * The human-meaningful name behind a grant key, by looking the key up rather
 * than taking it apart (TECH-3336):
 *   - the workspace's own "everyone" key → "everyone" (the baseline grant,
 *     TECH-2941, docs/adr/11-workspace-participation-permission-structure.md)
 *   - a roster role's key                → that role's name
 *   - anything else                      → "", which the helpers above read as
 *     "everyone". A channel pseudo-role key lands here, as it did before, and
 *     so does a key naming a role this roster has no row for.
 */
function roleNameForKey(roleKey: string, lookup: RoleKeyLookup): string {
	const found = lookup.classify(roleKey);
	if (found.kind === "everyone") return "everyone";
	if (found.kind === "role") return found.role.name;
	return "";
}

const STATUS_PRIORITY: Record<DisplayActivityStatus, number> = {
	active: 0,
	"in-broadcast": 1,
	"in-voice": 2,
	idle: 3,
	offline: 4,
};

function sortUsersByPresence(
	users: UserEntry[],
	broadcastParticipants: Set<string>
): UserEntry[] {
	return [...users].sort((a, b) => {
		const statusA = getDisplayActivityStatus(
			a.presence,
			broadcastParticipants.has(a.profile.firebaseUid)
		);
		const statusB = getDisplayActivityStatus(
			b.presence,
			broadcastParticipants.has(b.profile.firebaseUid)
		);
		const diff = STATUS_PRIORITY[statusA] - STATUS_PRIORITY[statusB];
		if (diff !== 0) return diff;
		const nameA = a.profile.displayName.toLowerCase();
		const nameB = b.profile.displayName.toLowerCase();
		return nameA.localeCompare(nameB);
	});
}

function assignUsersToHighestRole(
	roleKeys: string[],
	usersMap: Map<string, UserEntry>,
	lookup: RoleKeyLookup,
	channelId: string
): Map<string, UserEntry[]> {
	const roleKeySet = new Set(roleKeys);
	const result = new Map<string, UserEntry[]>();

	for (const [, userEntry] of usersMap) {
		// A user's roles are refs. Named ones carry the key the server put on their
		// roster row (TECH-3336) and are matched against the channel's grants.
		const userRoleKeys = lookup.keysForRefs(userEntry.roles);
		const grantedByRole = userRoleKeys.some((key) => roleKeySet.has(key));

		// The channel's own pseudo-role, which is how one user is given one
		// channel, is matched on the REF. It has no roster row and therefore no key
		// to compare, and dropping it would empty this list of everyone whose only
		// way in is that grant.
		const grantedByChannel = userEntry.roles.some(
			(ref) => ref.kind === "channel" && ref.channelId === channelId
		);

		if (!(grantedByRole || grantedByChannel)) continue;

		// Grouped under the highest-priority role they hold. Each key is named
		// once here, and everything after this point works in names. Someone here
		// only through the channel pseudo-role holds no named role, and falls to
		// "everyone", as they did before.
		const userRoleNames = userRoleKeys.map((key) =>
			roleNameForKey(key, lookup)
		);
		const bestRoleName = userRoleNames.reduce(
			(best, current) =>
				roleNamePriority(current) < roleNamePriority(best) ? current : best,
			userRoleNames[0] ?? ""
		);

		const groupName = canonicalGroupName(bestRoleName);
		const existing = result.get(groupName) ?? [];
		existing.push(userEntry);
		result.set(groupName, existing);
	}

	const sortedEntries = Array.from(result.entries()).sort(
		([a], [b]) => roleNamePriority(a) - roleNamePriority(b)
	);

	return new Map(sortedEntries);
}

function useChannelMembers(channelId: string) {
	const channel = useStore(coreStore, (state) => state.channels.get(channelId));
	const usersMap = useStore(coreStore, (state) => state.users);
	const lookup = useRoleKeys(channel?.workspaceId);
	const broadcast = useStore(coreStore, (state) => state.broadcast);

	return useMemo(() => {
		// Role keys, not the full ACL. `channel.list` sends only the keys to `/app`
		// (docs/adr/24-what-the-channel-list-sends-to-the-client.md), which is all
		// the grouping needs, because a user's group is their highest role rather
		// than a permission value.
		const channelRoleKeys = channel?.roleKeys;
		if (!channelRoleKeys)
			return {
				roleGroups: [],
				totalCount: 0,
				onlineCount: 0,
				broadcastParticipants: new Set<string>(),
			};

		const broadcastParticipants = new Set([
			...(broadcast?.invitedSpeakers ?? []),
			...(broadcast?.startedBy ? [broadcast.startedBy] : []),
		]);

		const usersByRole = assignUsersToHighestRole(
			channelRoleKeys,
			usersMap,
			lookup,
			channelId
		);

		const seenUsers = new Set<string>();
		for (const [, users] of usersByRole) {
			for (const u of users) {
				seenUsers.add(u.profile.firebaseUid);
			}
		}

		const onlineCount = Array.from(usersMap.values()).filter(
			(u) =>
				seenUsers.has(u.profile.firebaseUid) && u.presence?.status === "online"
		).length;

		const roleGroups = Array.from(usersByRole.entries())
			.map(([groupName, users]) => ({
				groupName,
				label: formatRoleName(groupName),
				users: sortUsersByPresence(users, broadcastParticipants),
			}))
			.reverse();

		return {
			roleGroups,
			totalCount: seenUsers.size,
			onlineCount,
			broadcastParticipants,
		};
	}, [channel?.roleKeys, channelId, usersMap, broadcast, lookup]);
}

export function ChannelPeoplePopover() {
	const { channelId, selectedChannel } = useChannelContext();
	const permissionChannelId = selectedChannel.parentId ?? channelId;
	const { roleGroups, totalCount, onlineCount, broadcastParticipants } =
		useChannelMembers(permissionChannelId);
	const channels = useStore(coreStore, (state) => state.channels);

	// Grouping needs every user's roles. The connect chain loads only the caller's
	// roles (users.myRoles), so the full matrix is lazy-loaded here on open.
	// Depending on the flags self-heals if a reconnect clears the matrix while the
	// popover is open, and the action is fetch-once guarded.
	const [open, setOpen] = useState(false);
	const allRolesLoaded = useStore(coreStore, (state) => state.allRolesLoaded);
	const allRolesLoading = useStore(coreStore, (state) => state.allRolesLoading);
	const fetchAllUserRoles = useStore(
		coreStore,
		(state) => state.fetchAllUserRoles
	);
	useEffect(() => {
		if (open && !(allRolesLoaded || allRolesLoading)) {
			fetchAllUserRoles();
		}
	}, [open, allRolesLoaded, allRolesLoading, fetchAllUserRoles]);

	return (
		<Popover onOpenChange={setOpen} open={open}>
			<Tooltip>
				<TooltipTrigger asChild>
					<PopoverTrigger asChild>
						<Button className="gap-1.5" size="sm" variant="ghost">
							<Users className="size-4" />
							{/* The real count needs the full role matrix, which loads lazily on
							    open. Until then show no count rather than the caller-only "1",
							    and a spinner while it is fetching. */}
							{allRolesLoaded && (
								<span className="text-muted-foreground text-xs">
									{totalCount}
								</span>
							)}
							{!allRolesLoaded && allRolesLoading && (
								<Loader2 className="size-3 animate-spin text-muted-foreground" />
							)}
						</Button>
					</PopoverTrigger>
				</TooltipTrigger>
				<TooltipContent>Channel Members</TooltipContent>
			</Tooltip>
			<PopoverContent align="end" className="w-64 p-0" side="bottom">
				<div className="flex items-center justify-between border-b px-3 py-2">
					<span className="font-medium text-sm">Members</span>
					{allRolesLoaded && (
						<span className="text-muted-foreground text-xs">
							{onlineCount} online
						</span>
					)}
				</div>
				<div className="max-h-80 overflow-y-auto p-1">
					{!allRolesLoaded && (
						<div className="flex items-center justify-center py-8">
							<Loader2 className="size-5 animate-spin text-muted-foreground" />
						</div>
					)}
					{allRolesLoaded &&
						roleGroups.map((group) => (
							<div key={group.groupName}>
								<div className="px-2 pt-2 pb-1 font-medium text-muted-foreground text-xs uppercase tracking-wide">
									{group.label} — {group.users.length}
								</div>
								{group.users.map((entry) => {
									const status = getDisplayActivityStatus(
										entry.presence,
										broadcastParticipants.has(entry.profile.firebaseUid)
									);
									const displayName =
										entry.profile.displayName || "Unknown User";
									return (
										<Tooltip key={entry.profile.firebaseUid}>
											<TooltipTrigger asChild>
												<div className="flex h-8 items-center gap-2 rounded-md px-2 hover:bg-accent">
													<PresenceIndicator status={status} />
													<span className="truncate text-sm">
														{displayName}
													</span>
													<ProfileOrganization
														className="min-w-0"
														uid={entry.profile.firebaseUid}
													/>
												</div>
											</TooltipTrigger>
											<TooltipContent side="left">
												<p>{getStatusLabel(status)}</p>
												{entry.presence?.currentChannelId && (
													<p className="text-muted-foreground text-xs">
														In:{" "}
														{channels.get(entry.presence.currentChannelId)
															?.name ?? "Voice"}
													</p>
												)}
											</TooltipContent>
										</Tooltip>
									);
								})}
							</div>
						))}
					{allRolesLoaded && roleGroups.length === 0 && (
						<div className="px-3 py-4 text-center text-muted-foreground text-sm">
							No members found
						</div>
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
}
